type WebViewWindow = Window & {
  ReactNativeWebView?: { postMessage: (msg: string) => void };
};

export function postToNative(payload: Record<string, unknown>): boolean {
  const bridge = (globalThis as unknown as WebViewWindow).ReactNativeWebView;
  if (!bridge) return false;
  bridge.postMessage(JSON.stringify(payload));
  return true;
}

// Signal the native host to fully close Courtney: hang up any live call and
// dismiss the WebView back to the floating trigger — parity with the web
// widget's X (setOpen(false)) and the native pill's own close button. This is
// deliberately distinct from the deep-link auto-minimize, which the native host
// drives itself (setView('minimized')) and which keeps the session alive.
//
// Version skew: land the native `courtney:dismiss` handler BEFORE shipping this
// so the X never becomes a no-op against an older native build. Until then the
// native host still receives the old `courtney:minimize` from prior web builds.
export function requestClose(): void {
  postToNative({ type: "courtney:dismiss" });
}

// Report the user's live voice-call state to the native host so it can show or
// hide the orange call button on the minimized pill. Additive + skew-tolerant:
// older native builds ignore the unknown message type.
export function emitVoiceState(active: boolean): boolean {
  return postToNative({ type: "courtney:voicestate", active });
}

// Parse a raw native->web message and report whether it carries the given
// type. Defensive against non-JSON, non-object, and unknown-type payloads.
function hasType(raw: string, type: string): boolean {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return false;
  }
  if (parsed === null || typeof parsed !== "object") {
    return false;
  }
  return (parsed as { type?: unknown }).type === type;
}

// Native->web end-call command (the native pill's close button hangs up).
export function isEndCall(raw: string): boolean {
  return hasType(raw, "courtney:endcall");
}

// Native->web start-call command: the native pill's phone button asks the page
// to switch to voice mode. Additive + skew-tolerant: older web builds simply
// never subscribed, so the message is ignored and native's expand still shows
// the chat.
export function isStartCall(raw: string): boolean {
  return hasType(raw, "courtney:startcall");
}

// Ask the native host to perform navigation instead of letting the WebView
// navigate itself. On Android a real <a> navigation tears the /mobile page
// down (unmounting AgentSession → @livekit/components-react's useSession runs
// room.disconnect() in cleanup), ending the call the instant a deep-link card
// is tapped. Posting the URL keeps the page mounted and the session alive; the
// native host does the routing. Returns true when a bridge is present (i.e. we
// are inside the native WebView).
//
// `intent: "checkout"` marks a handoff to an external checkout: the native
// host ends the call and closes the chat instead of keeping the session
// warm behind the browser. Additive + skew-tolerant: older native builds
// ignore the extra field.
export type NavigationIntent = "checkout";

export function requestNativeNavigation(
  url: string,
  intent?: NavigationIntent,
): boolean {
  return postToNative(
    intent
      ? { type: "courtney:navigate", url, intent }
      : { type: "courtney:navigate", url },
  );
}

// Click handler for card CTAs. Always stops propagation (cards sit inside
// horizontally-scrollable containers). Inside the native WebView it prevents
// the anchor's real navigation and routes via the bridge instead; on the
// regular web widget requestNativeNavigation returns false, so the
// <a target="_blank"> opens a new tab exactly as before.
type CtaClickEvent = { stopPropagation: () => void; preventDefault: () => void };
export function handleCardCtaClick(
  e: CtaClickEvent,
  url: string,
  intent?: NavigationIntent,
): void {
  e.stopPropagation();
  if (requestNativeNavigation(url, intent)) {
    e.preventDefault();
  }
}

// Subscribe to a native->web command by message type. react-native-webview
// delivers ref.postMessage() as a "message" event whose `data` is the JSON
// string — on `window` on iOS but on `document` on Android, so subscribe to
// both. The WeakSet keeps the handler from double-firing should one event
// ever reach both targets. Returns an unsubscribe fn.
function messageOfType(raw: string, type: string): unknown | undefined {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return undefined;
  }
  if (parsed === null || typeof parsed !== "object" || !("type" in parsed)) {
    return undefined;
  }
  if (parsed.type !== type) return undefined;
  return parsed;
}

function onNativeMessage(type: string, handler: (payload: unknown) => void): () => void {
  const seen = new WeakSet<Event>();
  const listener = (event: Event) => {
    if (seen.has(event)) {
      return;
    }
    seen.add(event);
    const data = (event as MessageEvent).data;
    if (typeof data !== "string") return;
    const payload = messageOfType(data, type);
    if (payload !== undefined) handler(payload);
  };
  const doc = typeof document !== "undefined" ? document : null;
  globalThis.addEventListener("message", listener);
  doc?.addEventListener("message", listener);
  return () => {
    globalThis.removeEventListener("message", listener);
    doc?.removeEventListener("message", listener);
  };
}

// Subscribe to native->web end-call commands. The native pill's close button
// posts { type: "courtney:endcall" }; the page stays loaded while the call is
// torn down.
export function onEndCall(handler: () => void): () => void {
  return onNativeMessage("courtney:endcall", handler);
}

// Subscribe to native->web start-call commands. The native pill's phone button
// posts { type: "courtney:startcall" } as it expands, asking the page to enter
// voice mode.
export function onStartCall(handler: () => void): () => void {
  return onNativeMessage("courtney:startcall", handler);
}

// Native pushed the open CourtsApp page. `page` is untrusted; the RPC
// handler sanitizes it before Courtney sees it.
export function onPageInfo(handler: (page: unknown) => void): () => void {
  return onNativeMessage("courtney:page", (payload) => {
    if (payload === null || typeof payload !== "object" || !("page" in payload)) {
      return;
    }
    handler(payload.page);
  });
}

// Ask the native host for the current page. Sent once the listener above is
// registered, so the reply is not lost to a load race.
export function requestCurrentPage(): void {
  postToNative({ type: "courtney:page-request" });
}
